import type { Direction, Theme, ThemeOptions } from '@material-ui/core';
import { createTheme, responsiveFontSizes } from '@material-ui/core/styles';
import merge from 'lodash/merge';
import { THEMES } from '../constants';
import { lightShadows } from './shadows';

interface ThemeConfig {
  direction?: Direction;
  responsiveFontSizes?: boolean;
  roundedCorners?: boolean;
  theme?: string;
}

let primaryColor = '#EE3235';

const whiteLabel: any = JSON.parse(localStorage.getItem('white_label'));
primaryColor =
  whiteLabel?.primaryColor?.length > 0 ? whiteLabel?.primaryColor : '#EE3235';

const baseOptions: ThemeOptions = {
  direction: 'ltr',
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '15px 10px',
          fontFamily: 'Inter',
          fontWeight: 400,
          fontSize: '14px',
          color: '#425466',
          borderBottom: '1px solid #EDF2F7',
        },
        head: {
          // Add your custom styles for table head cells here
          fontFamily: 'Inter',
          fontSize: 10,
          fontWeight: 600,
          color: '#8492A6',
          padding: 0,
          borderTop: '1px solid #EDF2F7',
        },
        body: {
          // Add your custom styles for table body cells here
          fontSize: '14px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          padding: 0,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#FAFAFB',
          fontSize: '14px', // Change header background color
          color: '#8492A6',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 0,
          '&.Mui-checked': {
            '& svg': {
              color: primaryColor,
            },
          },
          '& svg': {
            color: '#C9CED6',
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          // Add your custom styles for the backdrop root here
          // For example:
          backgroundColor: 'transparent', // Set the backdrop color
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: '75%',
          width: '75%',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
        },
        '#root': {
          height: '100%',
        },
        '#nprogress .bar': {
          zIndex: '2000 !important',
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h5',
          fontSize: '16px',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '16px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow:
            '0px 3px 8px -1px rgba(50, 50, 71, 0.05), 0px 0px 1px 0px rgba(12, 26, 75, 0.24)',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '& .MuiAccordionSummary-root': {
            // background: '#231F20',
            // color: '#fff',
            // borderRadius: '0  0 4px 4px  ',
            padding: '0 20px',
          },
          '& .MuiAccordionSummary-expandIconWrapper ': {
            // color: '#fff',
          },
          '& .MuiAccordionSummary-content.Mui-expanded ': {
            margin: 0,
          },
          '& .MuiAccordionSummary-content': {
            margin: 0,
          },
          // '& .MuiButtonBase-root.Mui-expanded': {
          //   minHeight: '39px',
          // },
          '& .MuiButtonBase-root': {
            // minHeight: '39px',
            // borderRadius: '10px',
          },
          '& .MuiAccordionDetails-root': {
            padding: '0 16px 16px',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiButtonBase-root': {
            minHeight: 'initial',
          },
        },
      },
    },
  },
  typography: {
    fontFamily: 'Inter',
    button: {
      fontWeight: 600,
    },
    h1: {
      fontWeight: 600,
      fontSize: '3.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '3rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h4: {
      fontWeight: 500,
      fontSize: '1.3rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '0.88rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.7rem',
    },
    overline: {
      fontWeight: 600,
    },
  },
};

const themesOptions: Record<string, ThemeOptions> = {
  [THEMES.LIGHT]: {
    components: {
      MuiInputBase: {
        styleOverrides: {
          root: {
            background: 'white',
          },
          input: {
            '&::placeholder': {
              opacity: 0.86,
              color: '#42526e',
            },
          },
        },
      },
    },
    palette: {
      action: {
        active: primaryColor,
      },
      background: {
        default: '#F7FAFC',
        paper: '#ffffff',
      },
      error: {
        contrastText: '#ffffff',
        main: primaryColor,
      },
      mode: 'light',
      primary: {
        contrastText: '#ffffff',
        main: primaryColor,
      },
      success: {
        contrastText: '#ffffff',
        main: primaryColor,
      },
      text: {
        primary: '#231F20',
        secondary: '#000',
      },
      warning: {
        contrastText: '#ffffff',
        main: primaryColor,
      },
      secondary: {
        contrastText: '#ffffff',
        main: primaryColor,
      },
    },
    shadows: lightShadows,
  },
};

export const createCustomTheme = (config: ThemeConfig = {}): Theme => {
  let themeOptions = themesOptions[config.theme];

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    themeOptions = themesOptions[THEMES.LIGHT];
  }

  let theme = createTheme(
    merge(
      {},
      baseOptions,
      themeOptions,
      {
        ...(config.roundedCorners && {
          shape: {
            borderRadius: 4,
          },
        }),
      },
      {
        direction: config.direction,
      }
    )
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
